import { useCallback, useEffect, useState } from "react";
import { matchPath, useLocation, useSearchParams } from "react-router-dom";
import { useApi } from "../useApi";
import { isErr } from "../../Utils/Result";
import { Tour, TOURS } from "../../../Components/Tour/tours";

/** `?tour=<id>` starts that tour even when it is done, to try it out locally. */
const FORCE_TOUR_PARAM = "tour";

/**
 * Picks the guided tour to show on the current page, if any, and marks it as
 * done once the user finishes or dismisses it.
 *
 * Which tours are done comes from the backend, not from the user stored at
 * login, so a tour done on one device is not shown again on another.
 *
 * @returns `activeTour`, the tour to show now (undefined while the done ones
 *   load, or if they could not be loaded), and `completeTour`, to call when it
 *   ends whichever way.
 */
export const useTours = () => {
  const { getClientUserEndpoint, updateClientUserEndpoint } = useApi();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [completedTours, setCompletedTours] = useState<string[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const response = await getClientUserEndpoint();
      if (cancelled || isErr(response)) return;
      setCompletedTours(response.value.clientUser.completedTours);
    })();
    return () => {
      cancelled = true;
    };
  }, [getClientUserEndpoint]);

  const forcedTourId = searchParams.get(FORCE_TOUR_PARAM);
  const isOnTourRoute = (tour: Tour) =>
    tour.routes.some((route) => matchPath(route, location.pathname) !== null);

  const activeTour =
    completedTours === null
      ? undefined
      : TOURS.find(
          (tour) =>
            isOnTourRoute(tour) && (tour.id === forcedTourId || !completedTours.includes(tour.id))
        );

  const completeTour = useCallback(
    (tourId: string) => {
      setCompletedTours((previous) =>
        previous && !previous.includes(tourId) ? [...previous, tourId] : previous
      );
      setSearchParams(
        (params) => {
          if (params.get(FORCE_TOUR_PARAM) === tourId) params.delete(FORCE_TOUR_PARAM);
          return params;
        },
        { replace: true }
      );
      // If it fails the user gets the tour once more next time, not worth telling them.
      void updateClientUserEndpoint({ completedTours: [tourId] });
    },
    [setSearchParams, updateClientUserEndpoint]
  );

  return { activeTour, completeTour };
};
